import { type ReactNode } from "react";
import { ChevronLeft, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface DetailStickyTopBarProps {
  /** Record label shown centered (e.g. order id or customer name). */
  title: string;
  /** Back button — typically navigates to the list route. */
  onBack: () => void;
  /** Optional close (X) button on the right — typically also navigates to the list. */
  onClose?: () => void;
  /** Extra controls shown left of the close button on desktop (e.g. layout switcher). */
  rightExtra?: ReactNode;
  className?: string;
}

/**
 * Shared sticky top bar for full-page detail views (back · title · optional
 * controls · close). Used by both the generic full-page detail
 * (GenericDetailContent) and the bespoke StandardDetailPage detail pages so the
 * chrome is identical everywhere. Negative horizontal margins cancel the
 * PageContainer padding so it spans edge-to-edge; it sticks at top-0 so the full
 * bar stays visible when scrolled.
 */
export function DetailStickyTopBar({
  title,
  onBack,
  onClose,
  rightExtra,
  className,
}: DetailStickyTopBarProps) {
  return (
    <div
      className={cn(
        // Negative margins fully cancel the PageContainer padding so the bar sits
        // flush to the top/side edges: -mx cancels px-2/md:px-6, and -mt-4/md:-mt-6
        // cancels py-4/md:p-6 (md:-mt-4 left an ~8px top gap on desktop).
        "sticky top-0 z-30 -mx-2 md:-mx-6 -mt-4 md:-mt-6 mb-2 flex items-center justify-between h-14 border-b border-border/40 bg-white dark:bg-neutral-950 px-4 md:px-6",
        className,
      )}
    >
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors gap-0.5 text-sm md:-ml-1"
      >
        <ChevronLeft className="h-4 w-4" />
        <span className="md:inline hidden">Back to list</span>
        <span className="md:hidden">Back</span>
      </button>
      <div className="flex-1 min-w-0 text-center mx-2 md:mx-4">
        <h2 className="text-sm font-semibold truncate">{title}</h2>
      </div>
      <div className="flex items-center gap-1">
        {rightExtra && <div className="hidden md:flex">{rightExtra}</div>}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-muted-foreground/70 hover:text-foreground rounded-md p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
